import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useStyletron } from 'baseui';
import { Tabs, Tab } from 'baseui/tabs-motion';
import { Button, KIND, SIZE } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { DASHBOARD_RESOURCES, PAGE_NAMES } from 'constant';
import RiskBadge from '../../components/RiskBadge';
import useDetail from './useDetail';
import UsagesTab from './tabs/UsagesTab';
import FrontendTab from './tabs/FrontendTab';
import SegmentsTab from './tabs/SegmentsTab';
import RulesTab from './tabs/RulesTab';
import HistoryTab from './tabs/HistoryTab';

const ANALYZE_PATH = `/${PAGE_NAMES.dashboard}/${DASHBOARD_RESOURCES.configImpact}/analyze`;

const ConfigDetail: React.FC<{}> = () => {
	const { keyName } = useParams<{ keyName: string }>();
	const history = useHistory();
	const [css] = useStyletron();
	const [activeTab, setActiveTab] = React.useState<React.Key>('0');
	const { detail, loading, error } = useDetail(keyName);

	if (loading) {
		return (
			<div className={css({ display: 'flex', justifyContent: 'center', padding: '48px' })}>
				<Spinner />
			</div>
		);
	}

	if (error || !detail) {
		return (
			<div
				className={css({
					padding: '48px',
					textAlign: 'center' as const,
					color: '#E11900'
				})}>
				{error || 'Config key not found'}
			</div>
		);
	}

	return (
		<div className={css({ padding: '24px' })}>
			{/* Header */}
			<div
				className={css({
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					marginBottom: '24px'
				})}>
				<div>
					<div className={css({ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' })}>
						<h2 className={css({ margin: '0', fontSize: '22px' })}>{detail.keyName}</h2>
						<RiskBadge level={detail.riskLevel} />
					</div>
					<p className={css({ color: '#666', margin: '0 0 8px 0', fontSize: '14px' })}>{detail.description}</p>
					<div className={css({ display: 'flex', gap: '16px', fontSize: '13px', color: '#999' })}>
						<span>
							Type: <strong>{detail.dataType}</strong>
						</span>
						<span>
							Default: <code>{JSON.stringify(detail.defaultValue)}</code>
						</span>
						<span>
							Category: <strong>{detail.businessCategory}</strong>
						</span>
					</div>
				</div>
				<Button
					kind={KIND.primary}
					size={SIZE.compact}
					onClick={() => history.push(`${ANALYZE_PATH}?key=${encodeURIComponent(detail.keyName)}`)}>
					Analyze Impact
				</Button>
			</div>

			{/* Tabs */}
			<Tabs activeKey={activeTab} onChange={({ activeKey }) => setActiveTab(activeKey)}>
				<Tab title={`Usages (${detail.usages?.length || 0})`}>
					<UsagesTab usages={detail.usages} />
				</Tab>
				<Tab title={`Frontend (${detail.frontendMappings?.length || 0})`}>
					<FrontendTab mappings={detail.frontendMappings} />
				</Tab>
				<Tab title={`Segments (${detail.segmentInteractions?.length || 0})`}>
					<SegmentsTab interactions={detail.segmentInteractions} />
				</Tab>
				<Tab title={`Rules (${detail.businessRules?.length || 0})`}>
					<RulesTab rules={detail.businessRules} />
				</Tab>
				<Tab title={`History (${detail.recentChanges?.length || 0})`}>
					<HistoryTab changes={detail.recentChanges} />
				</Tab>
			</Tabs>
		</div>
	);
};

export default ConfigDetail;
